/* ============================================================
   MIMOSA ROYALE — ANIMATION ENGINE v2
   ============================================================ */

document.addEventListener('DOMContentLoaded', () => {

  /* ----------------------------------------------------------
     GSAP SETUP
  ---------------------------------------------------------- */
  if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
  }

  /* ----------------------------------------------------------
     SCROLL PROGRESS BAR
  ---------------------------------------------------------- */
  const progressBar = document.getElementById('scroll-progress');
  if (progressBar) {
    window.addEventListener('scroll', () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      progressBar.style.width = ((window.scrollY / max) * 100) + '%';
    }, { passive: true });
  }

  /* ----------------------------------------------------------
     SCROLL-BASED HEADER
  ---------------------------------------------------------- */
  const header = document.querySelector('.site-header');
  if (header) {
    window.addEventListener('scroll', () => {
      header.classList.toggle('scrolled', window.scrollY > 80);
    }, { passive: true });
  }

  /* ----------------------------------------------------------
     SECTION LABEL UNDERLINE DRAW
  ---------------------------------------------------------- */
  const labelObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('line-in');
        labelObserver.unobserve(e.target);
      }
    });
  }, { threshold: 0.5 });
  document.querySelectorAll('.section-label').forEach(el => labelObserver.observe(el));

  /* ----------------------------------------------------------
     MULTI-DIRECTION REVEAL  (data-reveal="up|left|right|scale|blur")
  ---------------------------------------------------------- */
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('in-view');
        revealObserver.unobserve(e.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
  document.querySelectorAll('[data-reveal]').forEach(el => revealObserver.observe(el));

  /* ----------------------------------------------------------
     STAGGER CHILDREN  (data-stagger on parent)
  ---------------------------------------------------------- */
  const staggerObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        const children = [...e.target.children];
        children.forEach((child, i) => {
          child.style.transitionDelay = (i * 0.09) + 's';
          child.classList.add('in-view');
        });
        staggerObserver.unobserve(e.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -32px 0px' });

  document.querySelectorAll('[data-stagger]').forEach(parent => {
    // set initial state on children
    [...parent.children].forEach(child => {
      if (!child.hasAttribute('data-reveal')) {
        child.setAttribute('data-reveal', 'up');
      }
    });
    staggerObserver.observe(parent);
  });

  /* ----------------------------------------------------------
     FADE-UP ANIMATIONS (existing class — keep backward compat)
  ---------------------------------------------------------- */
  if (typeof gsap !== 'undefined') {
    // GSAP handles fade-up for richer physics
    const fadeEls = document.querySelectorAll('.fade-up:not([data-gsap])');
    fadeEls.forEach(el => el.setAttribute('data-gsap', '1'));

    if (fadeEls.length) {
      // Group by parent section for staggered batches
      const sections = new Set();
      fadeEls.forEach(el => {
        const section = el.closest('section, .founders-section, .gifts-section') || el.parentElement;
        sections.add(section);
      });

      sections.forEach(section => {
        const els = section.querySelectorAll('.fade-up[data-gsap="1"]');
        if (!els.length) return;
        gsap.fromTo(els,
          { opacity: 0, y: 52, scale: 0.97 },
          {
            opacity: 1, y: 0, scale: 1,
            duration: 0.9,
            ease: 'power3.out',
            stagger: { amount: 0.45, from: 'start' },
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
              once: true,
            },
          }
        );
      });
    }
  } else {
    // Fallback without GSAP
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -32px 0px' });
    document.querySelectorAll('.fade-up').forEach(el => io.observe(el));
  }

  /* ----------------------------------------------------------
     REVEAL SAFETY NET — .fade-up (and split-title words) default to opacity:0.
     A missed ScrollTrigger (slow GSAP load, refresh race, fast scroll) must NEVER
     leave in-view content invisible. Rescue any stranded reveal to full opacity.
  ---------------------------------------------------------- */
  (function () {
    function rescue() {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      document.querySelectorAll('.fade-up, .section-title .word-inner').forEach(function (el) {
        if (parseFloat(getComputedStyle(el).opacity) >= 0.05) return;
        var r = el.getBoundingClientRect();
        if (r.top < vh * 0.92 && r.bottom > 0) {
          el.style.opacity = '1';
          el.style.transform = 'none';
          el.classList.add('visible');
        }
      });
    }
    var raf;
    window.addEventListener('scroll', function () { cancelAnimationFrame(raf); raf = requestAnimationFrame(rescue); }, { passive: true });
    setTimeout(rescue, 1800);
    setTimeout(rescue, 4200);
  })();

  /* ----------------------------------------------------------
     SECTION HEADINGS — GSAP WORD SPLIT REVEAL
  ---------------------------------------------------------- */
  if (typeof gsap !== 'undefined') {
    document.querySelectorAll('.section-title').forEach(el => {
      if (el.dataset.split) return;
      el.dataset.split = '1';

      // Split into word spans
      const words = el.innerHTML.split(/(<br\s*\/?>|\s+)/g);
      el.innerHTML = words.map(w => {
        if (!w.trim() || /^<br/.test(w)) return w;
        return `<span class="word-wrap"><span class="word-inner">${w}</span></span>`;
      }).join(' ');

      const inners = el.querySelectorAll('.word-inner');
      gsap.fromTo(inners,
        { y: '110%', opacity: 0 },
        {
          y: '0%', opacity: 1,
          duration: 0.75,
          ease: 'power3.out',
          stagger: 0.06,
          scrollTrigger: { trigger: el, start: 'top 85%', once: true },
        }
      );
    });
  }

  /* ----------------------------------------------------------
     COUNTER ANIMATION — hero stats + reviews aggregate
  ---------------------------------------------------------- */
  const countUp = (el, duration = 1400) => {
    const raw = el.textContent.trim();
    const num = parseInt(raw.replace(/\D/g, ''), 10);
    const suffix = raw.replace(/[\d]/g, '');
    if (!num) return;
    const finalText = num + suffix;
    // Reduced-motion users (or anything that can't animate): show the real value now.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.textContent = finalText;
      return;
    }
    const start = performance.now();
    let settled = false;
    // Safety net: if rAF stalls (backgrounded tab, janky device, fast scroll) the
    // counter must NEVER be left stranded on a wrong low number — force the real value.
    const guard = setTimeout(() => { if (!settled) { settled = true; el.textContent = finalText; } }, duration + 500);
    const step = (now) => {
      if (settled) return;
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      if (p < 1) {
        el.textContent = Math.round(eased * num) + suffix;
        requestAnimationFrame(step);
      } else {
        settled = true;
        clearTimeout(guard);
        el.textContent = finalText; // land exactly on the real value
      }
    };
    requestAnimationFrame(step);
  };

  const statIo = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        countUp(e.target);
        // trigger roll-in animation for agg-score
        e.target.classList.add('playing');
        statIo.unobserve(e.target);
      }
    });
  }, { threshold: 0.5 });

  document.querySelectorAll('.hero-stat-number, .awards-hero-stat-number, .agg-score').forEach(el => {
    statIo.observe(el);
  });

  /* ----------------------------------------------------------
     PARALLAX — hero image on scroll (desktop)
  ---------------------------------------------------------- */
  const hero = document.querySelector('.hero');
  const heroImg = hero?.querySelector('.hero-products-image');
  if (heroImg && typeof gsap !== 'undefined') {
    gsap.to(heroImg, {
      yPercent: 18,
      ease: 'none',
      scrollTrigger: {
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
      },
    });
  }

  /* ----------------------------------------------------------
     PARALLAX — occasion section background
  ---------------------------------------------------------- */
  if (typeof gsap !== 'undefined') {
    document.querySelectorAll('.occasion-card-image').forEach(img => {
      gsap.fromTo(img,
        { scale: 1.08, y: '-4%' },
        {
          y: '4%',
          ease: 'none',
          scrollTrigger: {
            trigger: img.closest('.occasion-card'),
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    });
  }

  /* ----------------------------------------------------------
     MOUSE PARALLAX ON HERO (desktop only)
  ---------------------------------------------------------- */
  if (hero && matchMedia('(hover: hover) and (pointer: fine)').matches) {
    const heroText    = hero.querySelector('.hero-content');
    const heroOverlay = hero.querySelector('.hero-image-overlay');
    let ticking = false, mx = 0, my = 0;

    hero.addEventListener('mousemove', (e) => {
      const r = hero.getBoundingClientRect();
      mx = (e.clientX - r.left  - r.width  / 2) / r.width;
      my = (e.clientY - r.top   - r.height / 2) / r.height;
      if (!ticking) {
        requestAnimationFrame(() => {
          if (heroImg && !gsap)    heroImg.style.transform    = `scale(1.06) translate(${mx * 18}px, ${my * 9}px)`;
          if (heroText)            heroText.style.transform   = `translate(${mx * -8}px, ${my * -4}px)`;
          if (heroOverlay)         heroOverlay.style.transform = `translate(${mx * 5}px, ${my * 3}px)`;
          ticking = false;
        });
        ticking = true;
      }
    });

    hero.addEventListener('mouseleave', () => {
      if (heroText)    heroText.style.transform   = '';
      if (heroOverlay) heroOverlay.style.transform = '';
    });
  }

  /* ----------------------------------------------------------
     MAGNETIC BUTTONS — subtle cursor attraction
  ---------------------------------------------------------- */
  if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
    document.querySelectorAll('.btn-primary, .btn-outline-white').forEach(btn => {
      btn.addEventListener('mousemove', (e) => {
        const r = btn.getBoundingClientRect();
        const x = e.clientX - r.left - r.width  / 2;
        const y = e.clientY - r.top  - r.height / 2;
        btn.style.transform = `translate(${x * 0.22}px, ${y * 0.22}px)`;
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
    });
  }

  /* ----------------------------------------------------------
     RIPPLE EFFECT ON ALL BUTTONS
  ---------------------------------------------------------- */
  document.querySelectorAll('.btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const r = btn.getBoundingClientRect();
      const size = Math.max(r.width, r.height);
      const ripple = document.createElement('span');
      ripple.className = 'ripple';
      ripple.style.cssText = `
        width: ${size}px; height: ${size}px;
        left: ${e.clientX - r.left - size / 2}px;
        top: ${e.clientY - r.top - size / 2}px;
      `;
      btn.appendChild(ripple);
      ripple.addEventListener('animationend', () => ripple.remove());
    });
  });

  /* ----------------------------------------------------------
     GSAP — award cards stagger
  ---------------------------------------------------------- */
  if (typeof gsap !== 'undefined') {
    const awardCards = document.querySelectorAll('.award-card, .award-item');
    if (awardCards.length) {
      gsap.fromTo(awardCards,
        { opacity: 0, y: 40, scale: 0.94 },
        {
          opacity: 1, y: 0, scale: 1,
          duration: 0.65,
          ease: 'power3.out',
          stagger: 0.06,
          scrollTrigger: { trigger: awardCards[0].closest('section'), start: 'top 82%', once: true },
        }
      );
    }

    // Gift cards stagger
    const giftCards = document.querySelectorAll('.gift-card');
    if (giftCards.length) {
      gsap.fromTo(giftCards,
        { opacity: 0, y: 36 },
        {
          opacity: 1, y: 0,
          duration: 0.7,
          ease: 'power3.out',
          stagger: 0.1,
          scrollTrigger: { trigger: giftCards[0].closest('section'), start: 'top 80%', once: true },
        }
      );
    }

    // Feature items stagger
    const featureItems = document.querySelectorAll('.feature-item');
    if (featureItems.length) {
      gsap.fromTo(featureItems,
        { opacity: 0, y: 28, scale: 0.95 },
        {
          opacity: 1, y: 0, scale: 1,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.08,
          scrollTrigger: { trigger: '.features-bar', start: 'top 85%', once: true },
        }
      );
    }

    // Press bar items
    const pressItems = document.querySelectorAll('.press-bar-item');
    if (pressItems.length) {
      gsap.fromTo(pressItems,
        { opacity: 0, y: 16 },
        {
          opacity: 0.7, y: 0,
          duration: 0.55,
          ease: 'power2.out',
          stagger: 0.07,
          scrollTrigger: { trigger: '.press-trust-bar', start: 'top 90%', once: true },
        }
      );
    }

    // Urgency strip items
    const urgencyItems = document.querySelectorAll('.urgency-item');
    if (urgencyItems.length) {
      gsap.fromTo(urgencyItems,
        { opacity: 0, x: -20 },
        {
          opacity: 1, x: 0,
          duration: 0.5,
          ease: 'power2.out',
          stagger: 0.1,
          scrollTrigger: { trigger: '.urgency-strip', start: 'top 88%', once: true },
        }
      );
    }

    // Founders header
    const fHeader = document.querySelector('.founders-header');
    if (fHeader) {
      gsap.fromTo(fHeader,
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0,
          duration: 0.9,
          ease: 'power3.out',
          scrollTrigger: { trigger: fHeader, start: 'top 82%', once: true },
        }
      );
    }

    // Founders hero image dramatic entrance
    const fHeroImg = document.querySelector('.founders-hero-img');
    if (fHeroImg) {
      gsap.fromTo(fHeroImg,
        { opacity: 0, scale: 0.92, y: 40 },
        {
          opacity: 1, scale: 1, y: 0,
          duration: 1.1,
          ease: 'expo.out',
          scrollTrigger: { trigger: fHeroImg, start: 'top 82%', once: true },
        }
      );
    }

    // Founder cards dramatic stagger
    const founderCards = document.querySelectorAll('.founder-card');
    if (founderCards.length) {
      gsap.fromTo(founderCards,
        { opacity: 0, y: 50, scale: 0.92, rotateY: 8 },
        {
          opacity: 1, y: 0, scale: 1, rotateY: 0,
          duration: 0.75,
          ease: 'power3.out',
          stagger: 0.12,
          scrollTrigger: { trigger: '.founders-team-grid', start: 'top 82%', once: true },
        }
      );
    }

    // Reviews header split
    const reviewsHeader = document.querySelector('.reviews-header');
    if (reviewsHeader) {
      const children = [...reviewsHeader.children];
      gsap.fromTo(children[0],
        { opacity: 0, x: -40 },
        { opacity: 1, x: 0, duration: 0.8, ease: 'power3.out',
          scrollTrigger: { trigger: reviewsHeader, start: 'top 82%', once: true } }
      );
      if (children[1]) {
        gsap.fromTo(children[1],
          { opacity: 0, x: 40, scale: 0.92 },
          { opacity: 1, x: 0, scale: 1, duration: 0.8, delay: 0.1, ease: 'power3.out',
            scrollTrigger: { trigger: reviewsHeader, start: 'top 82%', once: true } }
        );
      }
    }

    // VIP section title dramatic entrance
    const vipTitle = document.querySelector('.vip-title');
    if (vipTitle) {
      gsap.fromTo(vipTitle,
        { opacity: 0, y: 60, scale: 0.9 },
        {
          opacity: 1, y: 0, scale: 1,
          duration: 1.1,
          ease: 'expo.out',
          scrollTrigger: { trigger: vipTitle, start: 'top 80%', once: true },
        }
      );
    }

    // Subscribe strip slide-in
    const subStrip = document.querySelector('.subscribe-strip-inner');
    if (subStrip) {
      gsap.fromTo(subStrip,
        { opacity: 0, y: 30 },
        {
          opacity: 1, y: 0,
          duration: 0.85,
          ease: 'power3.out',
          scrollTrigger: { trigger: subStrip, start: 'top 85%', once: true },
        }
      );
    }
  }

  /* ----------------------------------------------------------
     MOBILE NAV
  ---------------------------------------------------------- */
  const hamburger   = document.querySelector('.hamburger');
  const mobileNav   = document.querySelector('.mobile-nav');
  const mobileClose = document.querySelector('.mobile-nav-close');

  if (hamburger && mobileNav) {
    hamburger.addEventListener('click', () => {
      mobileNav.classList.add('open');
      document.body.style.overflow = 'hidden';
    });
  }
  if (mobileClose && mobileNav) {
    mobileClose.addEventListener('click', () => {
      mobileNav.classList.remove('open');
      document.body.style.overflow = '';
    });
  }
  if (mobileNav) {
    mobileNav.querySelectorAll('a').forEach(a => {
      a.addEventListener('click', () => {
        mobileNav.classList.remove('open');
        document.body.style.overflow = '';
      });
    });
  }

  /* ----------------------------------------------------------
     VANILLA TILT — 3D card tilt with gold glare
  ---------------------------------------------------------- */
  if (typeof VanillaTilt !== 'undefined') {
    VanillaTilt.init(document.querySelectorAll('.product-card'), {
      max: 8, speed: 450, glare: true, 'max-glare': 0.2, scale: 1.03, gyroscope: false,
    });
    VanillaTilt.init(document.querySelectorAll('.review-card'), {
      max: 4, speed: 600, glare: false, scale: 1.01, gyroscope: false,
    });
  }

  /* ----------------------------------------------------------
     CHAMPAGNE BUBBLE PARTICLES
  ---------------------------------------------------------- */
  if (hero) {
    const bc = document.createElement('div');
    bc.className = 'bubble-container';
    hero.appendChild(bc);
    const BUBBLE_COUNT = 55;
    for (let i = 0; i < BUBBLE_COUNT; i++) {
      const b = document.createElement('div');
      const size = 4 + Math.random() * 13;
      b.className = 'bubble' + (Math.random() < 0.2 ? ' gold' : '');
      b.style.cssText = `left:${Math.random() * 100}%;width:${size}px;height:${size}px;--dur:${5 + Math.random() * 6}s;--delay:${Math.random() * 10}s;opacity:${0.12 + Math.random() * 0.45};`;
      bc.appendChild(b);
    }
  }

  /* Custom gold cursor removed 2026-06-25 for performance: it ran a perpetual
     requestAnimationFrame loop + injected DOM/listeners on every desktop visit,
     but had zero matching CSS (it was visually inert). Pure main-thread waste. */

  /* ----------------------------------------------------------
     TICKER DUPLICATE (seamless loop)
  ---------------------------------------------------------- */
  const track = document.querySelector('.ticker-track');
  if (track) track.innerHTML += track.innerHTML;

  /* ----------------------------------------------------------
     SMOOTH ACTIVE NAV HIGHLIGHT
  ---------------------------------------------------------- */
  const currentPage = window.location.pathname.split('/').pop() || 'index.html';
  document.querySelectorAll('.nav-links a').forEach(a => {
    const href = a.getAttribute('href') || '';
    if (href === currentPage || (currentPage === 'index.html' && href === '#')) {
      a.classList.add('active');
    }
  });

  /* ----------------------------------------------------------
     RETAIL LOCATOR DEMO
  ---------------------------------------------------------- */
  const retailForm = document.getElementById('retail-form');
  const storeList  = document.getElementById('store-list');
  const demoStores = [
    { name: 'Total Wine & More',      addr: '1234 Main St',     dist: '0.8 mi', icon: '🍷' },
    { name: 'Kroger',                 addr: '567 Oak Ave',      dist: '1.2 mi', icon: '🛒' },
    { name: 'Whole Foods Market',     addr: '890 Market Blvd',  dist: '1.9 mi', icon: '🌿' },
    { name: 'BevMo!',                addr: '321 Commerce Dr',  dist: '2.4 mi', icon: '🍾' },
    { name: 'Cost Plus World Market', addr: '654 Elm St',       dist: '3.1 mi', icon: '🌍' },
  ];
  if (retailForm && storeList) {
    retailForm.addEventListener('submit', (e) => {
      e.preventDefault();
      storeList.innerHTML = demoStores.map(s => `
        <div class="store-item fade-up visible">
          <span class="store-icon">${s.icon}</span>
          <div><div class="store-name">${s.name}</div><div class="store-addr">${s.addr}</div></div>
          <span class="store-dist">${s.dist}</span>
        </div>`).join('');
    });
  }

  /* ----------------------------------------------------------
     PRODUCT ADD-TO-CART FEEDBACK
  ---------------------------------------------------------- */
  document.querySelectorAll('.product-add-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const orig = btn.innerHTML;
      btn.innerHTML = '✓';
      btn.style.background = '#22C55E';
      setTimeout(() => { btn.innerHTML = orig; btn.style.background = ''; }, 1200);
    });
  });

  /* ----------------------------------------------------------
     EMAIL + SMS MARKETING CAPTURES
  ---------------------------------------------------------- */
  (function() {
    const EMAIL_KEY = 'mr_email_captured';
    const POPUP_KEY = 'mr_popup_seen';

    function buildStickyBar() {
      if (localStorage.getItem(EMAIL_KEY)) return;
      if (document.getElementById('mr-sticky-bar')) return;
      const bar = document.createElement('div');
      bar.id = 'mr-sticky-bar';
      bar.className = 'sticky-capture-bar';
      bar.innerHTML = `
        <div class="sticky-bar-inner">
          <div class="sticky-bar-text">
            <span class="sticky-bar-headline">Join the VIP list — get 10% off your first order</span>
            <span class="sticky-bar-sub">Exclusive deals, new flavors &amp; early access. No spam, only bubbles.</span>
          </div>
          <form class="sticky-bar-form" id="sticky-bar-form" novalidate>
            <input type="email" class="sticky-bar-input" placeholder="Your email address" autocomplete="email" />
            <button type="submit" class="sticky-bar-btn">Get 10% Off</button>
          </form>
          <button class="sticky-bar-close" id="sticky-bar-close" aria-label="Dismiss">
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>`;
      document.body.appendChild(bar);
      requestAnimationFrame(() => requestAnimationFrame(() => bar.classList.add('active')));
      document.getElementById('sticky-bar-close').addEventListener('click', () => {
        bar.classList.remove('active'); setTimeout(() => bar.remove(), 450);
      });
      document.getElementById('sticky-bar-form').addEventListener('submit', (e) => {
        e.preventDefault();
        const input = bar.querySelector('.sticky-bar-input');
        if (!input.value || !input.value.includes('@')) { input.focus(); return; }
        localStorage.setItem(EMAIL_KEY, '1');
        bar.querySelector('.sticky-bar-form').innerHTML = '<span class="sticky-bar-thanks">You\'re in the VIP list. Check your inbox for 10% off. 🥂</span>';
        setTimeout(() => { bar.classList.remove('active'); setTimeout(() => bar.remove(), 450); }, 3000);
      });
    }

    // Email capture is now handled by the single 5s modal popup in theme.liquid.
    // Sticky bar + exit-intent popup disabled to avoid multiple competing popups.
    void buildStickyBar;
  })();

});
